import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { AppDispatch, RootState } from '../../redux/store';
import useCartActions from '../../hooks/use-cart-actions';
import Map from './components/map';
import PhoneForm from '../../views/account/credentials/phone';
import DeliveryProviders from './components/delivery-providers';
import EmptyPlaceholder from '../../components/empty-placholder';
import cartPlaceholder from '../../assets/images/empty-cart.svg';
import Api from '../../services';
import Alert from '../../components/alert';
import PaymentMethods from './components/payment-methods';
import OrderSummary from './components/order-summary';
import PaymentIframe from './components/payment-iframe';
import { fetchPurchaseQuote, resetPurchaseQuote } from '../../redux/purchase-quote';
import locationPlaceholder from '../../assets/images/location-placeholder.svg';
import CheckoutSkeleton from './components/checkout-skeleton';
import FullfillmentMethods, { FulfillmentMethod } from './components/fullfillment-methods';
import type { AddressDetails } from './components/national-address-form';
import usePaymentMethods from '../../hooks/use-payment-methods';
import useLocaleRefresh from '../../hooks/use-locale-refresh';

const TAMARA_PAYMENT_METHODS = ['PAY_BY_INSTALMENTS'];

interface CurrentPosition {
  latitude: number;
  longitude: number;
}

export default function Checkout() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { products } = useSelector((state: RootState) => state.cart);
  const profile = useSelector((state: RootState) => state.profile.data);
  const purchaseQuote = useSelector((state: RootState) => state.purchaseQuote.data);
  const authenticated = useSelector((state: RootState) => state.auth.authenticated);
  const { handleQuantityChange: updateCartQuantity, handleRemoveProduct, getCart } = useCartActions();
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isLocationPermissionEnabled, setIsLocationPermissionEnabled] = useState<boolean>(false);
  const [isErrorVisible, setIsErrorVisible] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [currentPosition, setCurrentPosition] = useState<CurrentPosition | null>(null);
  const [quoteFetched, setQuoteFetched] = useState(false);
  const [fullfillmentMethod, setFullfillmentMethod] = useState<'delivery' | 'pickup' | null>(null);
  const [purchaseId, setPurchaseId] = useState<string | null>(null);
  const [addressDetails, setAddressDetails] = useState<AddressDetails>({
    short_address: '',
    building_number: '',
    secondary_number: '',
    street_name: '',
    district: '',
    postal_code: '',
  });

  const handleAddressChange = (field: keyof AddressDetails, value: string) => {
    setAddressDetails((prev) => ({ ...prev, [field]: value }));
  };
  const dispatch = useDispatch<AppDispatch>();
  const cartId = localStorage.getItem('cart_id');
  const { fetchTamaraOptions, tamaraOptions } = usePaymentMethods();
  const handleQuantityChange = async (productId: number, increment: boolean) => {
    try {
      if (increment) {
        await updateCartQuantity(productId, true);
      } else {
        await updateCartQuantity(productId, false);
      }
      await dispatch(fetchPurchaseQuote({
        deliveryLat: currentPosition?.latitude,
        deliveryLon: currentPosition?.longitude,
        deliveryProviderId: purchaseQuote?.delivery?.delivery_provider_id,
        promoCode: purchaseQuote?.promotion?.code,
        cartId,
      }));
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleRemove = async (productId: number) => {
    try {
      await handleRemoveProduct(productId);
      await dispatch(fetchPurchaseQuote({
        deliveryLat: currentPosition?.latitude,
        deliveryLon: currentPosition?.longitude,
        deliveryProviderId: purchaseQuote?.delivery?.delivery_provider_id,
        promoCode: purchaseQuote?.promotion?.code,
        cartId,
      }));
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  };

  const handleFulfillmentMethodSelect = (method: FulfillmentMethod) => {
    setFullfillmentMethod(method);
    if (method === 'pickup') {
      dispatch(fetchPurchaseQuote({
        deliveryLat: currentPosition?.latitude,
        deliveryLon: currentPosition?.longitude,
        deliveryProviderId: null,
        promoCode: purchaseQuote?.promotion?.code,
        cartId,
      }));
    }
  };

  const handleCoordinatesChange = (lat: number, lng: number) => {
    setCurrentPosition({
      latitude: lat,
      longitude: lng,
    });

    if (currentPosition?.latitude !== lat || currentPosition?.longitude !== lng) {
      dispatch(fetchPurchaseQuote({
        deliveryLat: lat,
        deliveryLon: lng,
        deliveryProviderId: purchaseQuote?.delivery?.delivery_provider_id,
        promoCode: purchaseQuote?.promotion?.code,
        cartId,
      }));
    }
  };

  useLocaleRefresh(getCart);
  useEffect(() => {
    if (currentPosition && !quoteFetched) {
      dispatch(fetchPurchaseQuote({
        deliveryLat: currentPosition?.latitude,
        deliveryLon: currentPosition?.longitude,
        deliveryProviderId: purchaseQuote?.delivery?.delivery_provider_id,
        promoCode: purchaseQuote?.promotion?.code,
        cartId,
      }));
      setQuoteFetched(true);
    }
  }, [currentPosition, quoteFetched]);

  useEffect(() => {
    const total = purchaseQuote?.total?.amount;
    if (typeof total === 'number') {
      fetchTamaraOptions(total);
    }
  }, [purchaseQuote?.total?.amount]);

  useLocaleRefresh(() => {
    const total = purchaseQuote?.total?.amount;
    if (typeof total === 'number') {
      fetchTamaraOptions(total);
    }
  });
  const hasPhoneNumber = profile?.phone_number;

  const handleConfirmOrder = async () => {
    setSubmitting(true);
    setIsErrorVisible(false);

    const isTamaraSelected = selectedCardId !== null && TAMARA_PAYMENT_METHODS.includes(selectedCardId);

    const payload: any = {
      delivery_lat: currentPosition?.latitude,
      delivery_lon: currentPosition?.longitude,
      include_delivery: true,
      ecommerce_purchase: true,
      cart_id: cartId,
      signature: purchaseQuote?.signature,
      delivery_provider_id: purchaseQuote?.delivery?.delivery_provider_id,
      promo_code: purchaseQuote?.promotion?.code,
      ...addressDetails,
    };

    if (isTamaraSelected) {
      payload.payment_method = 'tamara';
      payload.payment_tamara_method = selectedCardId;
    } else {
      payload.payment_method = 'card';
      payload.payment_card_id = selectedCardId;
    }

    try {
      let response = await Api.purchases.purchaseCheckout(payload);
      const purchaseId = response.id;
      setPurchaseId(purchaseId);
      response = response.attributes;
      if (response.payment?.card_3ds_redirect_url) {
        setIframeUrl(response.payment.card_3ds_redirect_url);
      } else if (response.payment?.tamara_redirect_url) {
        setIframeUrl(response.payment.tamara_redirect_url);
      } else if (response.status === 'successful') {
        dispatch(resetPurchaseQuote());
        navigate(`/checkout/${purchaseId}`);
      } else {
        setIsErrorVisible(true);
      }
    } catch (error: any) {
      const errorData = error?.response?.data;
      const firstError = errorData?.errors?.[0];
      const errorMessage = firstError?.message || 'An unexpected error occurred';
      const errorCode = firstError?.type || 'unknown_error';

      setErrorText(errorMessage);
      if (errorCode === 'unavailable_product') {
        await getCart();
      }
      setIsErrorVisible(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleIframeMessage = (event: MessageEvent) => {
    const { data } = event;

    if (data?.purchaseStatus === 'successful') {
      setIframeUrl(null);
      dispatch(resetPurchaseQuote());
      navigate(`/checkout/${purchaseId}`);
    } else if (data?.purchaseStatus === 'failed') {
      setIframeUrl(null);
      navigate(`/checkout/${purchaseId}`);
    } else if (data?.event === 'ORDER_SUCCESS' && data?.data?.callbackUrl) {
      setIframeUrl(data.data.callbackUrl);
    } else if (data?.event === 'ORDER_FAILED') {
      setIframeUrl(null);
      setIsErrorVisible(true);
    }
  };

  useEffect(() => {
    if (iframeUrl) {
      window.addEventListener('message', handleIframeMessage);
    }
    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [iframeUrl]);

  const prevPhoneNumberRef = useRef(profile?.phone_number);

  useEffect(() => {
    if (!prevPhoneNumberRef.current && profile?.phone_number && typeof purchaseQuote?.total?.amount === 'number') {
      fetchTamaraOptions(purchaseQuote.total.amount);
    }
    prevPhoneNumberRef.current = profile?.phone_number;
  }, [profile?.phone_number, purchaseQuote?.total?.amount]);

  const requestLocationPermission = async () => {
    if (!navigator.geolocation) {
      setIsLocationPermissionEnabled(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentPosition({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setIsLocationPermissionEnabled(true);
      },
      () => {
        setIsLocationPermissionEnabled(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      },
    );
  };

  useEffect(() => {
    requestLocationPermission();
  }, []);

  if (!quoteFetched) {
    return <CheckoutSkeleton />;
  }

  if (!isLocationPermissionEnabled) {
    return (
      <EmptyPlaceholder
        title={t('checkout.locationPermission.title')}
        subtitle={t('checkout.locationPermission.subtitle')}
        imageSrc={locationPlaceholder}
      />
    );
  }

  if (products?.length === 0) {
    return (
      <EmptyPlaceholder
        title={t('cart.emptyTitle')}
        subtitle={t('cart.emptySubtitle')}
        imageSrc={cartPlaceholder}
      />
    );
  }

  const isDeliverySelected = fullfillmentMethod === 'delivery';
  const isDeliveryAvailable = (purchaseQuote?.delivery?.delivery_providers?.length ?? 0) > 0;
  const isAddressValid = /^[a-zA-Z]{4}\d{4}$/.test(addressDetails.short_address.trim());
  const addressErrors: Record<string, { message: string }> = {};

  if (isDeliverySelected) {
    if (addressDetails.short_address.trim() !== '' && !isAddressValid) {
      addressErrors.short_address = {
        message: t('checkout.nationalAddress.invalidShortAddress'),
      };
    } else if (addressDetails.short_address.trim() === '') {
      addressErrors.short_address = {
        message: t('checkout.nationalAddress.shortAddressRequired'),
      };
    }
  }

  const isSubmitDisabled = !!(
    !hasPhoneNumber
    || !fullfillmentMethod
    || (isDeliverySelected && (!currentPosition || !isDeliveryAvailable || !isAddressValid || !purchaseQuote?.delivery?.delivery_provider_id))
    || (purchaseQuote?.requires_payment && !selectedCardId)
    || submitting
  );

  return (
    <div className='min-h-screen bg-gray-50/50'>
      <div className='mx-auto max-w-7xl px-4 pb-24 pt-8 sm:px-6 lg:px-8'>
        <div className='lg:grid lg:grid-cols-12 lg:gap-x-12 xl:gap-x-16'>
          <div className='lg:col-span-7 xl:col-span-8 space-y-8'>
            {authenticated && !hasPhoneNumber && (
              <div className='bg-white shadow-sm rounded-2xl p-6 sm:p-8'>
                <h2 className='text-xl font-semibold text-gray-900 mb-6'>{t('checkout.addMobileNumber')}</h2>
                <PhoneForm />
              </div>
            )}

            {authenticated && hasPhoneNumber && (
              <>
                <div className='bg-white shadow-sm rounded-2xl p-6 sm:p-8'>
                  <FullfillmentMethods
                    onSelect={handleFulfillmentMethodSelect}
                    selectedOption={fullfillmentMethod}
                  />

                  {isDeliverySelected && (
                    <div className='mt-6 space-y-8'>
                      <div className='overflow-hidden border-b pb-4'>
                        <Map
                          onCoordinatesChange={handleCoordinatesChange}
                          addressDetails={addressDetails}
                          onAddressChange={handleAddressChange}
                          errors={addressErrors}
                        />
                        {!isDeliveryAvailable && currentPosition && (
                          <div className='bg-red-50 p-4 border-t border-red-200'>
                            <div className='flex'>
                              <div className='flex-shrink-0'>
                                <ExclamationTriangleIcon className='h-5 w-5 text-red-400' aria-hidden='true' />
                              </div>
                              <div className='ml-3'>
                                <h3 className='text-sm font-medium text-red-800'>
                                  {t('checkout.deliveryUnavailable.title') || 'Delivery Unavailable'}
                                </h3>
                                <div className='mt-2 text-sm text-red-700'>
                                  <p>{t('checkout.deliveryUnavailable.message') || 'Delivery is not available at this location. Please try another location or choose pickup.'}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {isDeliveryAvailable && (
                        <div className='animate-in fade-in slide-in-from-top-4 duration-500 delay-100'>
                          <DeliveryProviders currentPosition={currentPosition} />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {purchaseQuote?.requires_payment && (
                  <div className='bg-white shadow-sm rounded-2xl p-6 sm:p-8'>
                    <PaymentMethods
                      setSelectedCardId={setSelectedCardId}
                      selectedCardId={selectedCardId}
                      tamaraOptions={tamaraOptions}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <div className='lg:col-span-5 xl:col-span-4 mt-2 lg:mt-0'>
            <div className='sticky top-28'>
              <OrderSummary
                handleQuantityChange={handleQuantityChange}
                handleRemove={handleRemove}
                confirmOrder={handleConfirmOrder}
                currentPosition={currentPosition}
                loading={false}
                disableSubmit={isSubmitDisabled}
              />
            </div>
          </div>
        </div>
      </div>
      <PaymentIframe
        iframeUrl={iframeUrl}
        setIframeUrl={setIframeUrl}
      />
      <Alert
        title={t('purchaseStatus.failed.title')}
        text={errorText ?? t('purchaseStatus.failed.subtitle')}
        isAlertVisible={isErrorVisible}
        onClose={() => setIsErrorVisible(false)}
        type='error'
      />
    </div>
  );
}
